import { memo, useEffect, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { formatMinutesToTime } from '@/helpers';
import { cn } from '@/lib/utils';

import { DollarSignOffIcon } from '../../../../../assets/svgs/dollar-sign-off-icon';
import { ASSIGNMENT_BASE_OFFSET_REM, ASSIGNMENT_LEVEL_SPACING_REM } from '../../../constants';
import { ASSIGNMENT_TEXT_CLASSES } from '../../../constants/assignment-text';
import { PLANNER_STICKY_LEFT_PX } from '../../../constants/sizes';
import { useTextMeasurement } from '../../../contexts/text-measurement-context';
import { splitFragmentsAroundTimeOffs } from '../../../helpers/assignment-timeoff-fragments';
import { doesTextFitInBar } from '../../../helpers/assignment/assignment-text-overflow';
import usePlannerStore from '../../../store';
import { AssignmentType, TimeOffBlockType } from '../../../types';
import { calculateAssignmentOpacity, getAssignmentTextColor } from '../helpers/assignment-styles';

interface StickyLabelFragment {
  fragmentKey: string;
  assignment: AssignmentType;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  /** Bar width in rem */
  duration: number;
  /** Title text to display */
  title: string;
  /**
   * Vertical position of the bar this label belongs to (CSS `top`). Mirrors the
   * fragment's resolved top from `splitFragmentsAroundTimeOffs` so the label sits
   * exactly where the bar is — including the lower, centered position on a
   * partial APPROVED time-off day. Fully-cut approved days produce no fragment,
   * so no label is rendered over them.
   */
  top: string;
}

interface StickyAssignmentLabelsProps {
  assignments: AssignmentType[];
  timeOffs: TimeOffBlockType[];
  assignmentLevels: Map<number, number>;
  cellWidth: number;
  /**
   * Actual row height (px) — must match the overlay so the text label sits on the
   * same free-zone top as the repositioned assignment bar on partial time-off days.
   */
  cellHeight: number;
  /** Per-date capacity resolver (minutes/day) — must match the overlay so full-
   *  capacity approved days hide the label exactly where the bar is hidden. */
  getCapacityMinutes: (dateKey: string) => number;
  dates: dayjs.Dayjs[];
  onStickyFragmentsChange: (fragments: Map<number, Set<string>>) => void;
}

/**
 * Builds assignment fragments with pre-computed title and duration.
 *
 * Handles both continuous assignments (no weekend skips) and segmented assignments
 * (with active periods). Filters out fragments that are too short for sticky labels.
 */
const getLevelTop = (level: number): string =>
  `${level * ASSIGNMENT_LEVEL_SPACING_REM + ASSIGNMENT_BASE_OFFSET_REM}rem`;

const buildAssignmentFragments = (
  assignments: AssignmentType[],
  assignmentLevels: Map<number, number>,
  cellWidth: number,
  t: (key: string, options?: Record<string, unknown>) => string
): StickyLabelFragment[] => {
  return assignments.flatMap<StickyLabelFragment>(assignment => {
    const shouldRenderAsSingleBar = !assignment.originalAssignment.doSkipWeekdays;
    const title = t('assignmentLabel', {
      time: formatMinutesToTime(assignment.hoursPerDay * 60),
      title: assignment.title,
    });
    const top = getLevelTop(assignmentLevels.get(assignment.originalAssignment.id) ?? 0);

    if (shouldRenderAsSingleBar) {
      const days = assignment.endDate
        .startOf('day')
        .diff(assignment.startDate.startOf('day'), 'day');
      if (days < 1) return [];

      const duration = (days + 1) * cellWidth;

      return [
        {
          fragmentKey: `sticky-${assignment.originalAssignment.id}-continuous`,
          assignment,
          startDate: assignment.startDate,
          endDate: assignment.endDate,
          duration,
          title,
          top,
        },
      ];
    }

    // Segmented assignment with active periods
    return assignment.activePeriods
      .filter(period => {
        const days = period.endDate.startOf('day').diff(period.startDate.startOf('day'), 'day');
        return days >= 1;
      })
      .map((period, periodIndex) => {
        const days = period.endDate.startOf('day').diff(period.startDate.startOf('day'), 'day');
        const duration = (days + 1) * cellWidth;

        return {
          fragmentKey: `sticky-${assignment.originalAssignment.id}-${periodIndex}`,
          assignment,
          startDate: period.startDate,
          endDate: period.endDate,
          duration,
          title,
          top,
        };
      });
  });
};

/**
 * Filters fragments to only those where text fits within the bar.
 *
 * @returns Object with visible fragments and map of assignment ID -> fragment start dates with sticky labels
 */
const filterVisibleFragments = (
  fragments: StickyLabelFragment[],
  textReferenceElement: HTMLElement
): {
  visibleFragments: StickyLabelFragment[];
  stickyFragmentsByAssignment: Map<number, Set<string>>;
} => {
  const stickyFragmentsByAssignment = new Map<number, Set<string>>();
  const visible: StickyLabelFragment[] = [];

  // Group fragments by assignment ID to handle split assignments
  const fragmentsByAssignment = new Map<number, StickyLabelFragment[]>();
  for (const fragment of fragments) {
    const assignmentId = fragment.assignment.originalAssignment.id;
    const existing = fragmentsByAssignment.get(assignmentId) || [];
    existing.push(fragment);
    fragmentsByAssignment.set(assignmentId, existing);
  }

  // Process each assignment's fragments
  for (const [assignmentId, assignmentFragments] of fragmentsByAssignment.entries()) {
    const fragmentDates = new Set<string>();

    // Check each fragment individually
    for (const fragment of assignmentFragments) {
      const fits = doesTextFitInBar({
        assignment: fragment.assignment,
        barWidthRem: fragment.duration,
        text: fragment.title,
        textReferenceElement,
      });

      if (fits) {
        visible.push(fragment);
        fragmentDates.add(fragment.startDate.startOf('day').toISOString());
      }
    }

    if (fragmentDates.size > 0) {
      stickyFragmentsByAssignment.set(assignmentId, fragmentDates);
    }
  }

  return { visibleFragments: visible, stickyFragmentsByAssignment };
};

/**
 * Checks if sticky label should be hidden during interaction states.
 */
const shouldHideLabel = (
  assignmentId: number,
  interactingAssignmentId: number | null,
  activeCutId: number | null,
  activeDuplicateId: number | null
): boolean => {
  return (
    interactingAssignmentId === assignmentId ||
    activeCutId === assignmentId ||
    activeDuplicateId === assignmentId
  );
};

/**
 * Renders sticky text labels for multi-day assignment bars.
 *
 * Uses the same structural pattern as month headers: a flow-based container
 * constrains the sticky span to the assignment's horizontal range, while
 * position:sticky references the outer #planner-container scrollport.
 *
 * pointer-events-none ensures clicks pass through to the real assignment bars.
 *
 * When text overflows the bar width, the sticky label is hidden and the
 * original ScrollableText in the assignment bar is shown instead.
 */
export const StickyAssignmentLabels = memo(
  ({
    assignments,
    timeOffs,
    assignmentLevels,
    cellWidth,
    cellHeight,
    getCapacityMinutes,
    dates,
    onStickyFragmentsChange,
  }: StickyAssignmentLabelsProps) => {
    const { t } = useTranslation('authenticated.planner');
    const { assignmentTextRef } = useTextMeasurement();

    const {
      interactingAssignmentId,
      activeCutId,
      activeDuplicateId,
      hoveredAssignmentId,
      isDragResizeInteracting,
      showFilteredOutAssignments,
    } = usePlannerStore(
      useShallow(state => ({
        interactingAssignmentId: state.interactingAssignmentId,
        activeCutId: state.activeCutId,
        activeDuplicateId: state.activeDuplicateId,
        hoveredAssignmentId: state.hoveredAssignmentId,
        isDragResizeInteracting: state.isDragResizeInteracting,
        showFilteredOutAssignments: state.plannerPreferences.showFilteredOutAssignments ?? true,
      }))
    );

    const baseDate = dates[0];

    // Build fragments split around timeoff days so sticky labels don't overlap timeoff blocks
    const fragments = useMemo<StickyLabelFragment[]>(() => {
      if (!baseDate) return [];
      const rawFragments = buildAssignmentFragments(assignments, assignmentLevels, cellWidth, t);

      if (timeOffs.length === 0) return rawFragments;

      const overlayFragments = assignments.flatMap(assignment => {
        if (!assignment.originalAssignment.doSkipWeekdays) {
          return [
            {
              fragmentKey: `${assignment.originalAssignment.id}-continuous`,
              assignment,
              periodIndex: null as number | null,
            },
          ];
        }
        return assignment.activePeriods.map((_, periodIndex) => ({
          fragmentKey: `${assignment.originalAssignment.id}-${periodIndex}`,
          assignment,
          periodIndex,
        }));
      });

      // Use the SAME actual cell height and capacity resolver as the overlay so
      // the text label sits on the same per-day top as the assignment bar (and is
      // hidden on the same full-capacity approved days).
      const renderable = splitFragmentsAroundTimeOffs(
        overlayFragments,
        assignmentLevels,
        timeOffs,
        cellHeight,
        getCapacityMinutes
      );

      return renderable
        .filter(f => {
          const days = f.endDate.startOf('day').diff(f.startDate.startOf('day'), 'day');
          return days >= 1;
        })
        .map(f => {
          const days = f.endDate.startOf('day').diff(f.startDate.startOf('day'), 'day');
          const duration = (days + 1) * cellWidth;
          const title = t('assignmentLabel', {
            time: formatMinutesToTime(f.assignment.hoursPerDay * 60),
            title: f.assignment.title,
          });
          return {
            fragmentKey: `sticky-${f.key}`,
            assignment: f.assignment,
            startDate: f.startDate,
            endDate: f.endDate,
            duration,
            title,
            // Use the bar's resolved top (centered on a partial approved
            // time-off day) so the label tracks the bar instead of floating at
            // the level position over the time-off block.
            top: f.top,
          };
        });
    }, [
      assignments,
      baseDate,
      cellWidth,
      t,
      timeOffs,
      assignmentLevels,
      cellHeight,
      getCapacityMinutes,
    ]);

    // Determine which fragments have text that fits within the bar
    const { visibleFragments, stickyFragmentsByAssignment } = useMemo(() => {
      if (!assignmentTextRef) {
        return {
          visibleFragments: [],
          stickyFragmentsByAssignment: new Map<number, Set<string>>(),
        };
      }
      return filterVisibleFragments(fragments, assignmentTextRef);
    }, [fragments, assignmentTextRef]);

    // Notify parent of which fragments have active sticky labels
    // Filter out assignments that are hidden during interactions (cut/duplicate/drag)
    const activeStickyFragments = useMemo(() => {
      const filtered = new Map<number, Set<string>>();
      for (const [assignmentId, fragmentDates] of stickyFragmentsByAssignment.entries()) {
        if (
          !shouldHideLabel(assignmentId, interactingAssignmentId, activeCutId, activeDuplicateId)
        ) {
          filtered.set(assignmentId, fragmentDates);
        }
      }
      return filtered;
    }, [stickyFragmentsByAssignment, interactingAssignmentId, activeCutId, activeDuplicateId]);

    const prevFragmentsRef = useRef<Map<number, Set<string>>>(new Map());
    useEffect(() => {
      const prev = prevFragmentsRef.current;

      // Check if maps are different
      let changed = prev.size !== activeStickyFragments.size;
      if (!changed) {
        for (const [id, dates] of activeStickyFragments.entries()) {
          const prevDates = prev.get(id);
          if (
            !prevDates ||
            prevDates.size !== dates.size ||
            ![...dates].every(d => prevDates.has(d))
          ) {
            changed = true;
            break;
          }
        }
      }

      if (changed) {
        prevFragmentsRef.current = activeStickyFragments;
        onStickyFragmentsChange(activeStickyFragments);
      }
    }, [activeStickyFragments, onStickyFragmentsChange]);

    // Hide the entire sticky labels layer during interactions that show phantom elements
    // (drag/resize, duplicate, create-by-drag). Uses CSS invisible (not unmounting) so layout
    // stays stable and no blink occurs. The Assignment component mirrors this by reading the
    // same store values to show ScrollableText in the same render frame.
    const hasActivePhantom = isDragResizeInteracting || activeDuplicateId !== null;

    return (
      <div
        className={cn('pointer-events-none absolute inset-0 z-[3]', {
          invisible: hasActivePhantom,
        })}
      >
        {visibleFragments.map(fragment => {
          const { assignment, startDate, fragmentKey, duration, title, top } = fragment;
          const assignmentId = assignment.originalAssignment.id;

          // Hide during interactions (drag/resize/cut/duplicate)
          if (
            shouldHideLabel(assignmentId, interactingAssignmentId, activeCutId, activeDuplicateId)
          ) {
            return null;
          }

          // Hide filtered-out assignment labels when the preference is disabled
          const isFilteredOut = assignment.isFilteredOut || !assignment.belongsToTheGroup;
          if (isFilteredOut && !showFilteredOutAssignments) {
            return null;
          }

          const startOffset =
            startDate.startOf('day').diff(baseDate.startOf('day'), 'day') * cellWidth;

          const textColor = getAssignmentTextColor(assignment.status, assignment.color);
          const opacity = calculateAssignmentOpacity({
            isResizing: false,
            isDragging: false,
            showPhantom: false,
            disabled: !!assignment.disabled,
            isFilteredOut: assignment.isFilteredOut || !assignment.belongsToTheGroup,
            isCutting: false,
            isDuplicating: false,
          });

          // Match the hover lift from the assignment bar
          const isHovered =
            hoveredAssignmentId === assignmentId &&
            !isDragResizeInteracting &&
            activeCutId === null &&
            activeDuplicateId === null;

          return (
            <div
              key={fragmentKey}
              className={cn(
                'absolute left-0 right-0 flex h-7 items-center transition-transform duration-150',
                { '-translate-y-1': isHovered }
              )}
              style={{ top }}
            >
              {/* Inner flow div constrains sticky range to the assignment's horizontal span */}
              <div
                className="flex h-full items-center overflow-clip"
                style={{
                  marginLeft: `${startOffset}rem`,
                  width: `${duration}rem`,
                }}
              >
                {/* Sticky left = PLANNER_STICKY_LEFT_PX (sidebar width + gap) */}
                <span
                  className={cn(
                    'sticky z-[3] whitespace-nowrap px-3 truncate',
                    ASSIGNMENT_TEXT_CLASSES
                  )}
                  style={{
                    left: `${PLANNER_STICKY_LEFT_PX}px`,
                    color: textColor,
                    opacity,
                    maxWidth: `${duration}rem`,
                  }}
                >
                  <span className="flex items-center gap-0.5">
                    {!assignment.originalAssignment.isBillable && (
                      <DollarSignOffIcon size={14} className="flex-shrink-0" />
                    )}
                    {title}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);
